import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Group,
  Paper,
  Text,
  Stack,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import QuizDocumentAcknowledgment from "../../components/quiz/QuizDocumentAcknowledgment";
import QuizTimerBar from "../../components/quiz/QuizTimerBar";
import QuizQuestionCard from "../../components/quiz/QuizQuestionCard";

const QuizTake = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [api] = useServerApi();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [active, setActive] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [attempt, setAttempt] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reloadPrep = useCallback(async () => {
    const prepRes = await api.Quiz.getPrep(id);
    const prep = prepRes?.data;
    if (!prep?.quiz) return null;
    setQuizData((prev) => ({
      ...prep,
      questions: prev?.questions?.length ? prev.questions : [],
    }));
    return prep;
  }, [id]);

  useEffect(() => {
    let mounted = true;

    const loadQuiz = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const prepRes = await api.Quiz.getPrep(id);
        if (!mounted) return;

        if (!prepRes?.success || !prepRes?.data?.quiz) {
          setQuizData(null);
          setLoadError(prepRes?.message ?? "Quiz not available");
          return;
        }

        const prep = prepRes.data;

        if (prep.latestResult?.status === "IN_PROGRESS") {
          const takeRes = await api.Quiz.getTake(id);
          if (!mounted) return;

          if (!takeRes?.success || !takeRes?.data) {
            setQuizData(prep);
            setLoadError(takeRes?.message ?? "Unable to resume quiz");
            return;
          }

          setQuizData(takeRes.data);
          setActive(true);
          setAttempt(takeRes.data.latestResult);
          return;
        }

        setQuizData({ ...prep, questions: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadQuiz();
    return () => {
      mounted = false;
    };
  }, [id]);

  const questions = quizData?.questions ?? [];
  const documents = quizData?.documents ?? [];
  const acknowledgments = quizData?.acknowledgments ?? [];
  const questionCount = quizData?.questionCount ?? questions.length;
  const allAcked =
    !documents.length ||
    documents.every((doc) =>
      acknowledgments.some(
        (a) => String(a.document) === String(doc._id) && a.acknowledgedAt
      )
    );

  const attemptLabel = useMemo(() => {
    const count = quizData?.attemptCount ?? 0;
    const max = quizData?.quiz?.maxRetakeCount ?? 100;
    return `Attempt ${Math.max(1, count + 1)} of ${max}`;
  }, [quizData]);

  const handleDownload = async (docId) => {
    const doc = documents.find((d) => d._id === docId);
    await api.Quiz.downloadDocument(docId, doc?.fileName);
    await reloadPrep();
  };

  const handleAcknowledge = async (docId) => {
    await api.Quiz.acknowledgeDocument(docId);
    await reloadPrep();
  };

  const handleStart = async () => {
    const res = await api.Quiz.start(id);
    if (!res?.success) {
      showNotification({
        title: "Cannot start",
        message: res?.message ?? "Failed to start quiz",
        color: "red",
      });
      return;
    }
    setQuizData((prev) => ({
      ...prev,
      questions: res.data?.questions ?? [],
      latestResult: res.data?.result,
    }));
    setAttempt(res.data?.result);
    setActive(true);
    setCurrentIndex(0);
    setAnswers({});
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const payload = questions.map((q) => ({
      question: q._id,
      selectedOption: answers[q._id],
    }));
    const res = await api.Quiz.submit(id, payload);
    setSubmitting(false);
    if (res?.success) {
      navigate(`/consultant/myQuizzes/${id}/result`, {
        state: { review: res.data },
      });
    }
  };

  const handleExpire = useCallback(() => {
    if (!submitting) handleSubmit();
  }, [submitting, questions, answers]);

  if (loading) {
    return (
      <Page title="Quiz">
        <Text color="dimmed">Loading...</Text>
      </Page>
    );
  }

  if (!quizData?.quiz) {
    return (
      <Page title="Quiz">
        <Text color="dimmed">{loadError ?? "Quiz not found or not available."}</Text>
      </Page>
    );
  }

  if (!active) {
    return (
      <Page title={quizData.quiz.title}>
        {loadError && (
          <Text color="red" mb="md">
            {loadError}
          </Text>
        )}
        <QuizDocumentAcknowledgment
          documents={documents}
          acknowledgments={acknowledgments}
          onDownload={handleDownload}
          onAcknowledge={handleAcknowledge}
        />

        <Paper p="xl" withBorder radius="md" mt="md">
          <Stack align="center" spacing="md">
            <Title order={3}>Ready to Start?</Title>
            <Text>{quizData.quiz.title}</Text>
            <Text size="sm" color="dimmed">
              Time Limit:{" "}
              {quizData.quiz.testDuration > 0
                ? `${quizData.quiz.testDuration} minutes`
                : "Unlimited"}
            </Text>
            <Text size="sm" color="dimmed">
              {questionCount} Questions
            </Text>
            <Text size="sm" color="dimmed" align="center">
              Once you start, the timer will begin. You must complete all
              questions before the time expires.
            </Text>
            <Text size="sm">{attemptLabel}</Text>
            <Button size="lg" disabled={!allAcked} onClick={handleStart}>
              Start Quiz
            </Button>
          </Stack>
        </Paper>
      </Page>
    );
  }

  const currentQuestion = questions[currentIndex];
  const isLast = currentIndex >= questions.length - 1;

  return (
    <Page title={quizData.quiz.title} hideTitle>
      <QuizTimerBar
        testDuration={quizData.quiz.testDuration}
        startedAt={attempt?.startedAt}
        currentIndex={currentIndex}
        totalQuestions={questions.length}
        onExpire={handleExpire}
      />

      {currentQuestion && (
        <QuizQuestionCard
          question={currentQuestion}
          selectedOption={answers[currentQuestion._id]}
          onSelect={(option) =>
            setAnswers((prev) => ({
              ...prev,
              [currentQuestion._id]: option,
            }))
          }
        />
      )}

      <Group position="apart" mt="xl">
        <Button
          variant="default"
          disabled={currentIndex === 0}
          onClick={() => setCurrentIndex((i) => i - 1)}
        >
          Previous
        </Button>
        {isLast ? (
          <Button loading={submitting} onClick={handleSubmit}>
            Submit Quiz
          </Button>
        ) : (
          <Button onClick={() => setCurrentIndex((i) => i + 1)}>Next</Button>
        )}
      </Group>
    </Page>
  );
};

export default QuizTake;
